import {
  checkDocumentRequirements,
  type ProjectType,
  type CommercialType
} from './projectRequirementsService';

/**
 * Result of document validation check
 */
export interface ValidationResult {
  valid: boolean;
  missingDocuments: string[];
}

/**
 * Validates that required documents exist for a project before stage advancement.
 *
 * This function uses the centralized projectRequirementsService to determine
 * which documents are required based on project type and context.
 *
 * Fail-open design: Returns valid=true on query errors to prevent blocking users
 * due to infrastructure issues.
 *
 * @param projectId - Project UUID
 * @param projectType - 'Commercial' or 'Residential'
 * @param commercialType - 'Front of Meter' or 'Back of Meter' (if commercial)
 * @returns Validation result with list of missing documents
 *
 * @example
 * ```typescript
 * const validation = await validateRequiredDocuments(
 *   projectId,
 *   'Commercial',
 *   'Back of Meter'
 * );
 *
 * if (!validation.valid) {
 *   console.log('Missing:', validation.missingDocuments);
 * }
 * ```
 */
export async function validateRequiredDocuments(
  projectId: string,
  projectType: string,
  commercialType?: string | null
): Promise<ValidationResult> {
  try {
    // Use centralized requirements service
    const result = await checkDocumentRequirements(projectId, {
      projectType: projectType as ProjectType,
      commercialType: commercialType as CommercialType
    });

    return {
      valid: result.satisfied,
      missingDocuments: result.missing
    };
  } catch (error) {
    console.error('Error validating required documents:', error);
    // Fail open: allow advancement on errors to prevent blocking users
    return { valid: true, missingDocuments: [] };
  }
}
